/* ===================================================
   translateJob.js — the ONE translation loop (UI-agnostic).

   Why this exists: the loop used to live inside ConvertPage's closure and was stopped by a
   boolean ref. A boolean cannot interrupt an HTTP request or a pacing wait that is already
   running, and it could be flipped back by a later "Retry", waking an old loop. Every run is
   now an isolated JOB with its own id, AbortController and timers:

     * cancel()  -> aborts the in-flight fetch, clears every timer/countdown, mutes all later UI
                    callbacks, and tells the server to refuse any further provider call for the
                    job (POST /api/translate-cancel).
     * a cancelled job can never restart: Resume / Retry always create a NEW job (new id).
     * the client never retries on its own: it re-sends a chunk only when the server answers
       { retryable: true } and the shared budget allows it (max 1 retry == 2 attempts).
     * nothing here sleeps on the server's behalf -- the only waiting is cancellable pacing.
=================================================== */

// Mirrors the server's TRANSLATION_POLICY. The server is authoritative: every response carries
// attemptsUsed / attemptsMax and the server refuses a 3rd call regardless of this constant.
export const MAX_ATTEMPTS = 2;
const FALLBACK_GAP_MS      = 13000;   // only used until the first server response tells us the real gap
const REQUEST_TIMEOUT_MS   = 130000;  // > server's 90s provider timeout
const MAX_PACE_WAITS       = 4;       // bounded "pace" re-waits per chunk (these never call the provider)

export function newJobId() {
  try { if (window.crypto?.randomUUID) return window.crypto.randomUUID().replace(/-/g, ""); } catch { /* ignore */ }
  return "j" + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
}

class JobCancelled extends Error { constructor() { super("CANCELLED"); this.name = "JobCancelled"; } }
class JobError extends Error {
  constructor(type, message, extra = {}) { super(message); this.name = "JobError"; this.type = type; Object.assign(this, extra); }
}

/**
 * @param {object} p  { fileId, apiKey, provider, model, modelSelectionMode, batchSize, sourceLang, quality, pacing,
 *                      checkpoint, fileName }
 * @param {object} cb { emit(patch), saveCheckpoint(data), clearCheckpoint(), liveEnabled() -> bool, onChunkDone?(info) }
 * @returns {{ id, cancel(reason), isFinished(), isCancelled(), promise }}
 */
export function startTranslationJob(p, cb) {
  const id         = newJobId();
  const controller = new AbortController();
  const timers     = new Set();            // setTimeout / setInterval handles owned by this job
  const state      = { cancelled: false, finished: false, muted: false, saving: false, inFlight: false, requestsSent: 0, cancelReason: null };

  const emit = (patch) => { if (!state.muted) cb.emit(patch); };

  /* ---------- cancellation primitives ---------- */
  const throwIfCancelled = () => { if (state.cancelled || controller.signal.aborted) throw new JobCancelled(); };

  const clearTimers = () => {
    for (const t of timers) { clearTimeout(t); clearInterval(t); }
    timers.clear();
  };

  // Cancellable wait with a visible countdown. Resolves on time, rejects with JobCancelled on abort.
  const wait = (ms, onTick) => new Promise((resolve, reject) => {
    if (state.cancelled) return reject(new JobCancelled());
    let tick = null;
    const done = (fn) => { clearTimeout(to); if (tick) clearInterval(tick); timers.delete(to); if (tick) timers.delete(tick); controller.signal.removeEventListener("abort", onAbort); fn(); };
    const onAbort = () => done(() => reject(new JobCancelled()));
    const to = setTimeout(() => done(resolve), ms);
    timers.add(to);
    if (onTick) {
      const endAt = Date.now() + ms;
      tick = setInterval(() => onTick(Math.max(0, Math.ceil((endAt - Date.now()) / 1000))), 250);
      timers.add(tick);
      onTick(Math.ceil(ms / 1000));
    }
    controller.signal.addEventListener("abort", onAbort, { once: true });
  });

  // fetch bound to the job's signal + a per-request timeout. `cancellable=false` is used for the final save.
  async function request(url, init, { cancellable = true, timeoutMs = REQUEST_TIMEOUT_MS } = {}) {
    const local = new AbortController();
    let timedOut = false;
    const onAbort = () => local.abort();
    if (cancellable) {
      if (controller.signal.aborted) throw new JobCancelled();
      controller.signal.addEventListener("abort", onAbort, { once: true });
    }
    const to = setTimeout(() => { timedOut = true; local.abort(); }, timeoutMs);
    try {
      const res  = await fetch(url, { credentials: "include", ...init, signal: local.signal });
      const text = await res.text();
      let data = null;
      try { data = text ? JSON.parse(text) : null; } catch { data = null; }   // gateway HTML error pages etc.
      return { ok: res.ok, status: res.status, data };
    } catch (e) {
      if (cancellable && (state.cancelled || controller.signal.aborted)) throw new JobCancelled();
      const err = new Error(timedOut ? "timeout" : (e?.message || "network error"));
      err.isNetwork = true; err.timedOut = timedOut;
      throw err;
    } finally {
      clearTimeout(to);
      if (cancellable) controller.signal.removeEventListener("abort", onAbort);
    }
  }

  const sendServerCancel = () => {
    if (!state.requestsSent) return;               // nothing ever reached a provider route -> nothing to cancel there
    try {
      fetch("/api/translate-cancel", {
        method: "POST", credentials: "include", keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobId: id }),
      }).catch(() => {});
    } catch { /* ignore */ }
  };

  /* ---------- public cancel ---------- */
  function cancel(reason = "user") {
    if (state.finished || state.cancelled) return { cancelled: state.cancelled, wasInFlight: false };
    if (state.saving && reason !== "unmount" && reason !== "pagehide") return { cancelled: false, wasInFlight: false, blocked: "saving" };
    if (state.saving) return { cancelled: false, wasInFlight: false };   // leaving mid-save: let that single short request finish
    const wasInFlight = state.inFlight;
    state.cancelled = true; state.cancelReason = reason;
    controller.abort();                 // 1) abort the active browser request
    clearTimers();                      // 2) stop pacing waits, countdowns, retry timers
    sendServerCancel();                 // 3) server refuses any further provider call for this job + aborts the upstream request
    state.muted = true;                 // 4) nothing from this job may touch the UI again (the caller shows the cancelled state itself)
    return { cancelled: true, wasInFlight };
  }

  /* ---------- the loop ---------- */
  async function run() {
    const { fileId, apiKey, provider, model, modelSelectionMode, quality, pacing, sourceLang, fileName } = p;
    const isGemini = provider === "gemini";
    let translated = [];
    let gapMs      = FALLBACK_GAP_MS;
    let activeModel = null;              // ONLY ever set from a server response -- never guessed
    let lastCallStart = 0;

    try {
      /* STEP 1: subtitle texts (aborts with the job) */
      emit({ phase: "fetching", status: "File fetch ho rahi hai...", progress: 2, error: null });
      const f = await request(`/api/subtitle-texts/${encodeURIComponent(fileId)}`, { headers: {} });
      throwIfCancelled();
      if (!f.ok || !f.data) {
        if (f.status === 401) throw new JobError("session", "Session expire ho gaya.");
        if (f.status === 402 && f.data?.type === "ad_required") throw new JobError("ad_required", f.data.message || "Ad required");
        throw new JobError("fetch_failed", f.data?.message || "File fetch failed", { httpStatus: f.status });
      }
      const { subs, texts, total, ext, detectedLang } = f.data;
      emit({ totalLines: total, progress: 5, detectedLang });

      const chunkSize   = Math.min(Math.max(Number(p.batchSize) || 20, 1), p.maxBatch || 80);
      const totalChunks = Math.ceil(texts.length / chunkSize);

      /* RESUME: only from a checkpoint that provably belongs to this exact file */
      let startLine = 0;
      const cp = p.checkpoint;
      if (cp && Array.isArray(cp.translatedSoFar) && cp.translatedSoFar.length > 0 &&
          cp.totalLines === total && cp.translatedSoFar.length <= texts.length) {
        startLine  = cp.translatedSoFar.length;           // exact line count: nothing already translated is translated again
        translated = cp.translatedSoFar.slice(0, startLine);
        emit({ doneLines: startLine, progress: 5 + Math.round((startLine / texts.length) * 88),
               status: `✅ ${startLine} lines already done — resume ho raha hai...` });
        if (cb.liveEnabled() && startLine > 0)
          cb.onChunkDone?.({ reset: true, pairs: texts.slice(0, startLine).map((o, j) => ({ original: o, translated: translated[j] ?? "" })) });
      } else {
        emit({ doneLines: 0, status: `${total} lines | translate shuru...` });
      }

      // Only the translated lines matter for resuming (subs are fetched again). A write that did not reach
      // persistent storage is reported, never swallowed.
      let cpOk = true;
      const saveCp = () => {
        const ok = cb.saveCheckpoint({ translatedSoFar: translated, totalLines: total, ext, subs, fileName }) !== false;
        if (ok !== cpOk) { cpOk = ok; emit({ cpWarning: ok ? null : "storage" }); }
      };

      /* STEP 2: chunk loop */
      for (let start = startLine; start < texts.length; start += chunkSize) {
        throwIfCancelled();
        const chunk    = texts.slice(start, start + chunkSize);
        const chunkNum = Math.floor(start / chunkSize) + 1;
        let attempt = 1, paceWaits = 0, forcedWaitMs = 0, result = null;

        emit({ chunkNum, totalChunks, progress: 5 + Math.round((start / texts.length) * 88), doneLines: start, attempt: 1 });

        while (!result) {
          throwIfCancelled();

          /* --- ONE pacing strategy: wait out the agreed gap since the last provider call (cancellable) --- */
          const sinceLast = lastCallStart ? Date.now() - lastCallStart : Infinity;
          const needMs    = Math.max(lastCallStart ? gapMs - sinceLast : 0, forcedWaitMs);
          forcedWaitMs = 0;
          if (needMs >= 500) {
            emit({ phase: attempt > 1 ? "retrying" : "waiting_rpm", attempt, countdown: Math.ceil(needMs / 1000),
                   status: attempt > 1 ? `Retry ${attempt}/${MAX_ATTEMPTS} se pehle wait...` : "Rate-limit safe gap (pacing)..." });
            await wait(needMs, (sec) => emit({ countdown: sec }));
            emit({ countdown: null });
          }
          throwIfCancelled();

          emit({ phase: activeModel ? "translating" : "selecting_model", attempt,
                 status: `Chunk ${chunkNum}/${totalChunks}${attempt > 1 ? ` — retry ${attempt}/${MAX_ATTEMPTS}` : ""} translate ho raha hai...` });

          /* --- the request --- */
          let r;
          state.inFlight = true; state.requestsSent += 1; lastCallStart = Date.now();
          try {
            r = await request("/api/translate-chunk", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                apiKey, provider, texts: chunk,
                jobId: id, fileId, chunkStart: start, chunkNumber: chunkNum, totalChunks, attempt,
                // Gemini: manual => the exact pinned model; auto => last model the SERVER reported (sticky). Others: chosen model.
                model: isGemini ? (modelSelectionMode === "manual" ? model : (activeModel || undefined)) : model,
                modelSelectionMode: isGemini ? modelSelectionMode : undefined,
                quality, pacing, batchSize: chunkSize, sourceLang: sourceLang || null,
              }),
            });
          } catch (e) {
            if (e instanceof JobCancelled) throw e;
            r = { ok: false, status: 0, data: { type: "network", retryable: true, message: e.timedOut ? "Server ne time par jawab nahi diya." : "Network error." } };
          } finally { state.inFlight = false; }
          throwIfCancelled();                       // a response that arrives after cancel is discarded, never applied

          let d = r.data;
          if (!r.ok && !d) {                        // gateway page (502/503/504 HTML) or empty body
            d = { type: [502, 503, 504].includes(r.status) ? "network" : "error", retryable: [502, 503, 504].includes(r.status),
                  message: `Server error (${r.status || "no response"}).` };
          }
          if (d?.pace?.gapMs) gapMs = d.pace.gapMs;

          if (r.ok && Array.isArray(d?.translated) && d.translated.length === chunk.length) {
            result = d;
            break;
          }

          /* --- failure handling (all decisions in ONE place) --- */
          if (d?.type === "cancelled") throw new JobCancelled();
          if (d?.type === "pace") {                  // server says "too early" -> no provider call, no attempt used
            if (++paceWaits > MAX_PACE_WAITS) throw new JobError("rate_limit", "Pacing gap sync nahi ho raha (doosra tab same key use kar raha hai?).", { model: activeModel });
            forcedWaitMs = Math.max(500, d.retryAfterMs || gapMs);
            lastCallStart = 0;
            continue;
          }
          const attemptsMax = d?.attemptsMax || MAX_ATTEMPTS;
          const canRetry = !!d?.retryable && attempt < MAX_ATTEMPTS && (d?.attemptsUsed ?? attempt) < attemptsMax;
          if (canRetry) {
            attempt += 1;
            forcedWaitMs = d.retryAfterMs || 0;
            emit({ phase: "retrying", attempt, retryReason: d.type,
                   status: `${d.message || "Temporary error"} — ${MAX_ATTEMPTS - attempt + 1} retry baaki...` });
            continue;
          }
          throw new JobError(d?.type || "error", d?.message || "Chunk failed", { model: d?.model || activeModel, detail: d?.detail, attemptsUsed: d?.attemptsUsed });
        }

        /* --- chunk succeeded: apply the SERVER-reported model, then checkpoint --- */
        if (result.model) activeModel = result.model;
        const modelPatch = {
          model: activeModel, modelMode: result.modelSelectionMode, requestedModel: result.requestedModel || null,
          attempt: result.attempt || attempt,
        };
        if (result.modelChanged && result.switchReason) modelPatch.switchNotice = result.switchReason;   // stays visible until a new switch
        emit(modelPatch);
        translated.push(...result.translated);
        saveCp();                                               // only fully completed chunks are ever stored
        emit({ doneLines: start + chunk.length, progress: 5 + Math.round(((start + chunk.length) / texts.length) * 88), countdown: null });
        if (cb.liveEnabled()) cb.onChunkDone?.({ pairs: chunk.map((o, j) => ({ original: o, translated: result.translated[j] ?? "" })) });
      }

      /* STEP 3: save (short, final, deliberately not cancellable) */
      throwIfCancelled();
      if (translated.length !== texts.length) {
        saveCp();
        throw new JobError("invalid_response", `Translation incomplete: ${translated.length}/${texts.length} lines. Progress save hai — Resume karo.`);
      }
      state.saving = true;
      emit({ phase: "saving", status: "File save ho rahi hai...", progress: 95, countdown: null, attempt: null });
      let s;
      try {
        s = await request(`/api/save-translated/${encodeURIComponent(fileId)}`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ translated, ext, subs }),
        }, { cancellable: false, timeoutMs: 60000 });
      } catch (e) {
        throw new JobError("save_failed", "Save request fail hui (network).", { detail: e.message });
      }
      if (!s.ok) {
        if (s.status === 401) throw new JobError("session", "Session expire ho gaya.");
        throw new JobError("save_failed", s.data?.message || `Save failed (${s.status}).`);
      }

      state.finished = true;
      cb.clearCheckpoint();                                     // only after a confirmed save
      emit({ phase: "done", progress: 100, doneLines: texts.length, status: "Done!", downloadName: s.data?.downloadName || "" });
      return { status: "done", downloadName: s.data?.downloadName || "" };

    } catch (e) {
      state.finished = true;
      clearTimers();
      if (e instanceof JobCancelled) return { status: "cancelled", reason: state.cancelReason };
      if (e instanceof JobError) return { status: "error", error: { type: e.type, message: e.message, model: e.model || activeModel, detail: e.detail } };
      return { status: "error", error: { type: "error", message: e?.message || "Unexpected error", model: activeModel } };
    }
  }

  const promise = run();
  return { id, cancel, isFinished: () => state.finished, isCancelled: () => state.cancelled, promise };
}
